import Select from 'react-select/creatable'
import { selectStyles } from 'apps/builder/components/shared'
import { FancyRadio, FancyToggle } from 'apps/builder/components/settings/modal'
import { SettingsFieldSelect } from 'apps/builder/components/settings/modal/shared'
import { useDispatch, useSelector } from 'react-redux'
import { Input, TextArea } from 'components/Input/Input'
import { updateEntry } from 'apps/builder/store/actions/entries'
import { useState } from 'react'
import { builderTypeSelector } from 'apps/builder/store/selectors'
import {
  RequiredToggle,
  ReadOnlyToggle,
  SystemDefaultSelect
} from 'apps/builder/components/settings/modal/shared'
import { lib } from '@gocanvas/client'
const {
  TEXT_STYLE,
  TEXT_EMAIL_FORMAT_STYLE,
  TEXT_EMAIL_FORMAT_ALWAYS_STYLE,
  TEXT_NUMBER_FORMAT_STYLE,
} = lib

export const TextField = ({
  guid,
  style,
  label,
  required,
  read_only,
  default: defaultValue,
  type,
  original_type_id,
  keyField,
}) => {
  const dispatch = useDispatch()

  const updateDefaultValue = value => {
    dispatch(
      updateEntry({
        guid,
        default: value,
      })
    )
  }

  const inputProps = {
    value: defaultValue || '',
    placeholder: ' ',
    id: `${guid}-default-value`,
    update: updateDefaultValue,
    label: 'Default Value',
    additionalClass: 'settings--inputs',
  }

  const updateType = ({ guid, original_type_id, entry_type_id, type, style }) => {
    setType(original_type_id)
    dispatch(
      updateEntry({
        guid,
        original_type_id: original_type_id,
        entry_type_id: entry_type_id,
        type: type,
        style,
      })
    )
  }

  const [textStyle, setType] = useState(original_type_id)

  return (
    <>
      <div className="settings--row">
        <SettingsFieldSelect
          guid={guid}
          label={label}
          keyField={keyField}
          type={type}
        />
        <div className="toggles-group">
          <div className="toggle-wrapper">
            <RequiredToggle guid={guid} active={required} />
          </div>
          <div className="toggle-wrapper">
            <ReadOnlyToggle guid={guid} active={read_only} />
          </div>
        </div>
      </div>

      <div className="settings--format--wrapper">
        <label className="settings--label">Style</label>
        <FancyRadio
          id={`${guid}-1`}
          label={'Single Line'}
          selected={textStyle}
          value={0}
          onChange={(checked, value) => {
            checked
              ? updateType({
                  guid,
                  original_type_id: value,
                  entry_type_id: 10,
                  style: 0,
                  type: 'Text',
                })
              : ''
          }}
        />
        <FancyRadio
          id={`${guid}-2`}
          label={'Multiple Lines'}
          disabled={keyField}
          tooltip={keyField && `This style isn’t available for key fields.`}
          selected={textStyle}
          value={8}
          onChange={(checked, value) => {
            checked
              ? updateType({
                  guid,
                  original_type_id: value,
                  entry_type_id: 18,
                  style: 0,
                  type: 'Long Text',
                })
              : ''
          }}
        />
      </div>
      {original_type_id == 0 && (
        <FormatDropdown guid={guid} style={style} />
      )}
      {original_type_id == 0 ? (
        <>
          <div className="settings--default-value">
            <Input {...inputProps} />
          </div>
          <SystemDefaultSelect guid={guid} style={style} />
        </>
      ) : (
        <div className="settings--default-value">
          <TextArea {...inputProps} />
        </div>
      )}
    </>
  )
}

// temporary until https://gocanvas.atlassian.net/browse/GOC-16435
const builderOptions = [
  { value: TEXT_STYLE, label: 'None' },
  { value: TEXT_EMAIL_FORMAT_STYLE, label: 'Email' },
  { value: TEXT_NUMBER_FORMAT_STYLE, label: 'Number' },
]

const webFormOptions = [
  { value: TEXT_STYLE, label: 'None' },
  { value: TEXT_NUMBER_FORMAT_STYLE, label: 'Number' },
]

const FormatDropdown = ({ guid, style }) => {
  const dispatch = useDispatch()
  const isWebForm = useSelector(builderTypeSelector) === 'web-form'

  const parsedStyle = parseInt(style) || 0

  const options = isWebForm ? webFormOptions : builderOptions

  const isDisabled = !options
    .map(({ value }) => value)
    // The style for email when 'Always email submissions to this address' is selected
    .concat(TEXT_EMAIL_FORMAT_ALWAYS_STYLE)
    .includes(parsedStyle)

  const isEmailFormat = style =>
    [TEXT_EMAIL_FORMAT_STYLE, TEXT_EMAIL_FORMAT_ALWAYS_STYLE].includes(style)

  const value = isEmailFormat(parsedStyle)
    ? options[1]
    : options.find(({ value }) => value === parsedStyle)

  return (
    <div className="settings--text-format--wrapper">
      <div className="settings--field-type">
        <Select
          styles={selectStyles}
          className="input--placeholder"
          classNamePrefix="react-select"
          options={options}
          value={value}
          isDisabled={isDisabled}
          onChange={({ value }) => {
            dispatch(updateEntry({ guid, style: value }))
          }}
        />
        <label>Format</label>
      </div>
      {isEmailFormat(parsedStyle) && (
        <FancyToggle
          label="Always email submissions"
          tooltip="If checked, the email address entered into this field will receive the submission email. Mobile users will not be able to see or delete this email address when completing the submission."
          tooltipDirection="bottom"
          id={`${guid}--always-email-submissions`}
          active={parsedStyle === TEXT_EMAIL_FORMAT_ALWAYS_STYLE}
          inlineBorderStyle={true}
          onChange={checked => {
            dispatch(
              updateEntry({
                guid,
                style: checked
                  ? TEXT_EMAIL_FORMAT_ALWAYS_STYLE
                  : TEXT_EMAIL_FORMAT_STYLE,
              })
            )
          }}
        />
      )}
    </div>
  )
}
